/**
 * @prop --cbp-typography-color: inherit;
 * @prop --cbp-typography-color-dark: inherit;
 * @prop --cbp-typography-color-small-text: var(--cbp-color-text-darkest);
 * @prop --cbp-typography-color-small-text-dark: var(--cbp-color-text-lightest);
 * @prop --cbp-typography-color-large-text: var(--cbp-color-text-darker);
 * @prop --cbp-typography-color-large-text-dark: var(--cbp-color-text-lighter);
 * @prop --cbp-typography-color-divider-fill: var(--cbp-color-gray-cool-10);
 * @prop --cbp-typography-color-divider-fill-dark: var(--cbp-color-gray-cool-70);
 * @prop --cbp-typography-color-divider-underline: var(--cbp-color-gray-cool-30);
 * @prop --cbp-typography-color-divider-underline-dark: var(--cbp-color-gray-cool-50);
 */
:root {
  --cbp-typography-color: inherit;
  --cbp-typography-color-dark: inherit;
  --cbp-typography-color-small-text: var(--cbp-color-text-darkest);
  --cbp-typography-color-small-text-dark: var(--cbp-color-text-lightest);
  --cbp-typography-color-large-text: var(--cbp-color-text-darker);
  --cbp-typography-color-large-text-dark: var(--cbp-color-text-lighter);
  --cbp-typography-color-divider-fill: var(--cbp-color-gray-cool-10);
  --cbp-typography-color-divider-fill-dark: var(--cbp-color-gray-cool-70);
  --cbp-typography-color-divider-underline: var(--cbp-color-gray-cool-30);
  --cbp-typography-color-divider-underline-dark: var(--cbp-color-gray-cool-50);
}

// Displays dark design based on mode or context
[data-cbp-theme=light] cbp-typography[context*=dark],
[data-cbp-theme=dark] cbp-typography:not([context=dark-inverts]):not([context=light-always]) {
  --cbp-typography-color-small-text: var(--cbp-typography-color-small-text-dark) !important;
  --cbp-typography-color-large-text: var(--cbp-typography-color-large-text-dark) !important;
  --cbp-typography-color-divider-fill: var(--cbp-typography-color-divider-fill-dark) !important;
  --cbp-typography-color-divider-underline: var(--cbp-typography-color-divider-underline-dark) !important;
}

/* Inherits body text:
    var(--cbp-color-text-lightest)
    var(--cbp-color-text-darkest)

    h6 is same as body text, all others are lighter/darker
*/
cbp-typography {
  color: var(--cbp-typography-color);
  
  & > * {
    color: var(--cbp-typography-color);
  }

  &[variant=masthead-1] > *,
  &[variant=masthead-2] > *,
  &[variant=heading-xxl] > *, h1,
  &[variant=heading-xl] > *, h2,
  &[variant=heading-lg] > *, h3,
  &[variant=heading-md] > *, h4,
  &[variant=heading-sm] > *, h5 {
    --cbp-typography-color: var(--cbp-typography-color-large-text);
    //text-wrap: balance; // not at this time
  }

  
  &[divider=underline] {
    & > * {
      border-block-end: var(--cbp-typography-color-divider-underline) solid var(--cbp-border-size-md);
      padding-block: var(--cbp-space-3x);
    }

    &[variant*=masthead] > *,
    &[variant=heading-xxl] > * {
      padding-block-end: var(--cbp-space-4x);
      border-block-end-width: var(--cbp-border-size-xl);
    }

    &[variant=heading-sm] > *, h5 {
      border-block-end-width: var(--cbp-border-size-sm);
    }
    
    &[variant=heading-xs] > *, h6 {
      border-block-end-width: var(--cbp-border-size-sm);
      padding-block: var(--cbp-space-1x);
      padding-inline-end: var(--cbp-space-2x);
    }
  }

  &[divider=fill] {
    & > * {
      background-color: var(--cbp-typography-color-divider-fill);
      border-radius: var(--cbp-border-radius-soft);
      padding: var(--cbp-space-3x);
    }
    &[variant=heading-xs] > * , h6 {
      padding-block: var(--cbp-space-1x);
      padding-inline: var(--cbp-space-2x);
    }
  }

  &[divider] {
    &[variant=heading-md] > *, h4 {
      padding-block: var(--cbp-space-2x);
    }
    &[variant=heading-sm] > *, h5 {
      padding-block: var(--cbp-space-1x);
    }
  }


  &[variant=masthead-1] > * {
    font-size: var(--cbp-font-size-masthead-1);
    font-weight: var(--cbp-font-weight-thin);
    line-height: var(--cbp-space-11x);
  }

  &[variant=masthead-2] > * {
    font-size: var(--cbp-font-size-masthead-2);
    font-weight: var(--cbp-font-weight-thin);
    line-height: var(--cbp-space-10x);
  }

  &[variant=heading-xxl] > *, h1 {
    font-size: var(--cbp-font-size-heading-xxl);
    font-weight: var(--cbp-font-weight-light);
    line-height: var(--cbp-space-9x);
  }

  &[variant=heading-xl] > *, h2 {
    font-size: var(--cbp-font-size-heading-xl);
    font-weight: var(--cbp-font-weight-regular);
    line-height: var(--cbp-space-8x);
  }

  &[variant=heading-lg] > *, h3 {
    font-size: var(--cbp-font-size-heading-lg);
    font-weight: var(--cbp-font-weight-regular);
    line-height: var(--cbp-space-7x);
  }

  &[variant=heading-md] > *, h4 {
    font-size: var(--cbp-font-size-heading-md);
    font-weight: var(--cbp-font-weight-medium);
    line-height: var(--cbp-space-6x);
  }

  &[variant=heading-sm] > *, h5 {
    font-size: var(--cbp-font-size-heading-sm);
    font-weight: var(--cbp-font-weight-medium);
    line-height: var(--cbp-space-6x);
  }

  &[variant=heading-xs] > *, h6 {
    color: var(--cbp-typography-color-small-text);
    font-size: var(--cbp-font-size-heading-xs);
    font-weight: var(--cbp-font-weight-bold);
    line-height: var(--cbp-space-5x);
  }

  &[variant=body-text] > *, p {
    font-size: var(--cbp-font-size-body);
    font-weight: var(--cbp-font-weight-regular);
    line-height: var(--cbp-space-5x);
    max-width: var(--cbp-line-length-longer);
    text-wrap: pretty;
  }

  &[variant=subhead] > * {
    font-size: var(--cbp-font-size-subhead);
    font-weight: var(--cbp-font-weight-medium);
    line-height: var(--cbp-space-5x);
  }

  &[variant=heading-dialog] > * {
    display: block;
    font-size: var(--cbp-font-size-heading-lg);
    font-weight: var(--cbp-font-weight-bold);
    line-height: var(--cbp-line-height-md);
    padding: 0;
    padding-block-end: var(--cbp-space-2x);
    margin-block-end: var(--cbp-space-2x);
  }
}
